import { History, Languages, Trash2 } from "lucide-react";
import { LOGO_URL } from "@/constants/diplomate";
import { TRANSLATIONS, TONE_COLORS } from "@/i18n";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { QuotaBadge } from "@/components/diplomate/QuotaBadge";

export const AppHeader = ({ lang, onToggleLang, history, onClearHistory, onSelectHistory, quota }) => {
  const t = TRANSLATIONS[lang];
  return (
    <header className="border-b-2 border-black bg-white relative z-10" data-testid="app-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 sm:gap-4 min-w-0">
          <img
            src={LOGO_URL}
            alt="cordialement.ai"
            className="w-9 h-9 sm:w-11 sm:h-11 flex-shrink-0"
          />
          <div className="min-w-0 flex items-center gap-2 sm:gap-3">
            <h1 className="font-display font-semibold text-base sm:text-2xl leading-none tracking-tight uppercase truncate">
              CORDIALEMENT<span className="text-[#4A4A4A]">.</span>AI
            </h1>
            <span
              data-testid="brand-version"
              className="font-mono inline-flex items-center bg-black text-white text-[9px] sm:text-[10px] uppercase tracking-widest px-1.5 sm:px-2 py-0.5 flex-shrink-0"
            >
              v2.0
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <QuotaBadge quota={quota} lang={lang} />
          <HistorySheet
            t={t}
            history={history}
            onClearHistory={onClearHistory}
            onSelectHistory={onSelectHistory}
          />
          <button
            data-testid="lang-toggle-btn"
            onClick={onToggleLang}
            aria-label="Toggle language"
            className="bg-[#FFF2B2] border-2 border-black nb-shadow-sm nb-hover px-2.5 sm:px-3 py-2 font-bold text-xs sm:text-sm flex items-center gap-1.5"
          >
            <Languages className="w-4 h-4" />
            {t.langToggle}
          </button>
        </div>
      </div>
    </header>
  );
};

const HistorySheet = ({ t, history, onClearHistory, onSelectHistory }) => (
  <Sheet>
    <SheetTrigger asChild>
      <button
        data-testid="history-btn"
        className="bg-white border-2 border-black nb-shadow-sm nb-hover px-2.5 sm:px-3 py-2 font-bold text-xs sm:text-sm flex items-center gap-1.5"
      >
        <History className="w-4 h-4" />
        <span className="hidden lg:inline">{t.history}</span>
      </button>
    </SheetTrigger>
    <SheetContent className="bg-[#FBF9F6] border-l-2 border-black w-full sm:max-w-md">
      <SheetHeader>
        <SheetTitle className="font-display font-semibold text-2xl sm:text-3xl leading-tight tracking-tight">
          {t.history}
        </SheetTitle>
      </SheetHeader>
      <div className="mt-2 dossier-tag">Dossiers locaux · consultation restreinte</div>
      <div className="mt-6 flex justify-end">
        {history.length > 0 && (
          <button
            data-testid="clear-history-btn"
            onClick={onClearHistory}
            className="bg-[#FFDAC1] border-2 border-black nb-shadow-sm nb-hover px-3 py-1.5 font-bold text-[10px] uppercase tracking-widest flex items-center gap-1.5"
          >
            <Trash2 className="w-3.5 h-3.5" /> {t.clearHistory}
          </button>
        )}
      </div>
      <ScrollArea className="h-[calc(100vh-200px)] mt-4 pr-3">
        {history.length === 0 ? (
          <p className="text-[#4A4A4A] text-sm text-center mt-12 italic">
            {t.historyEmpty}
          </p>
        ) : (
          <ul className="space-y-3" data-testid="history-list">
            {history.map((item) => (
              <HistoryItem key={item.id} item={item} onSelect={onSelectHistory} />
            ))}
          </ul>
        )}
      </ScrollArea>
      <div className="mt-4 pt-4 border-t border-dashed border-black/30">
        <p className="text-[11px] text-[#4A4A4A] leading-relaxed">
          {t.historyLocalNotice}
        </p>
      </div>
    </SheetContent>
  </Sheet>
);

const HistoryItem = ({ item, onSelect }) => {
  const itemT = TRANSLATIONS[item.language];
  return (
    <li
      data-testid="history-item"
      onClick={() => onSelect(item)}
      className="cursor-pointer bg-white border-2 border-black p-4 nb-shadow-sm nb-hover"
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          className="font-mono text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border border-black"
          style={{ background: TONE_COLORS[item.tone] }}
        >
          {itemT?.tones[item.tone] || item.tone}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-wider text-[#4A4A4A]">
          {item.mode === "reformulate" ? itemT?.modeReformulate : itemT?.modeReply}
        </span>
      </div>
      <p className="text-sm text-[#4A4A4A] line-clamp-2 mb-1">{item.input}</p>
      <p className="text-sm font-medium line-clamp-3">→ {item.output}</p>
    </li>
  );
};
